import { escapeRegExp, isArticleEdited } from 'lib/utils'
import {
  CountryEdition,
  Filters,
  Game,
  InventoryArticle,
  PricedInventoryArticle,
  PRICE_CHANGE_TOO_SMALL,
} from 'shared'
import { CONDITIONS_LOOKUP } from 'utils/constants'

// The bot left the price as it was because the change was below the plan's minimum: the article
// is unchanged, not failed
const isPriceChangeTooSmall = (item: PricedInventoryArticle): boolean =>
  item.priceReport.failure === PRICE_CHANGE_TOO_SMALL

export const applyFilters = (
  filters: Filters,
  stock: InventoryArticle[] | PricedInventoryArticle[],
  activeGame: Game
): InventoryArticle[] => {
  const {
    edited,
    priceChanged,
    priceUnchanged,
    priceFailed,
    comment,
    location,
    name,
    condition,
    minQuantity,
    maxQuantity,
    minPrice,
    maxPrice,
    playset,
    language,
    selected,
    firstEd,
    exactPrice,
    expansion,
    signed,
    rarity,
    finishType,
    graded,
    american,
    sold,
  } = filters
  let finalStock = [...stock]

  if (!finalStock.length) {
    return stock
  }

  if (name) {
    /* 
    if the user has entered spaces or hyphens he may be searching for cn + set. 
    if the listing and appraisal search does not work, then try searching via the locNames field */
    const processed = false

    // if (name.includes('-') || name.includes(' ')) {
    //   const stockExpansions: LocalizedCard[] = []
    //   finalStock.forEach((item) => {
    //     const expansion = cardData.find((data) => {
    //       return data.cn === item.card.cn && data.set === item.card.set
    //     })
    //     if (expansion) stockExpansions.push(expansion)
    //   })

    //   const options = filterOptions(stockExpansions, name, 'en')

    //   const filteredStock = finalStock.filter((item) => {
    //     return options.find(
    //       (option) =>
    //         option.cn === item.card.cn &&
    //         option.name === item.card.name &&
    //         option.set === item.card.set
    //     )
    //   })

    //   if (filteredStock.length > 0) {
    //     finalStock = filteredStock
    //     processed = true
    //   }
    // }

    // Execute regular search if first has not results
    if (!processed) {
      const searchName = name.trim().toLowerCase()

      finalStock = finalStock.filter((item) => {
        const englishMatch = item.card.name.toLowerCase().includes(searchName)

        const italianMatch = item.card.locNames?.IT?.toLowerCase().includes(searchName)

        const spanishMatch = item.card.locNames?.ES?.toLowerCase().includes(searchName)

        const frenchMatch = item.card.locNames?.FR?.toLowerCase().includes(searchName)

        const germanMatch = item.card.locNames?.DE?.toLowerCase().includes(searchName)

        return englishMatch || italianMatch || spanishMatch || frenchMatch || germanMatch
      })
    }
  }

  if (comment) {
    const wordsToFind = comment
      .replace(/^!/, '')
      .replace(/^;/, '')
      .split('\n')
      .map((line) => escapeRegExp(line.trim()))
      .filter((line) => line)
      .join('|')

    const isEmptyCommentQuery =
      comment.replace('!', '') === '""' || comment.replace('!', '') === "''"

    const regExp = new RegExp(wordsToFind, 'i')
    if (isEmptyCommentQuery) {
      finalStock = finalStock.filter((stockItem) => {
        return comment[0] === '!'
          ? stockItem.currentComment !== ''
          : stockItem.currentComment === ''
      })
    } else if (comment[0] === '!') {
      finalStock = finalStock.filter(
        (stockItem) => !regExp.test(stockItem.newComment || stockItem.comment)
      )
    } else if (comment[0] === ';') {
      const exactWord = comment.slice(1).trim()

      finalStock = finalStock.filter((stockItem) => stockItem.currentComment === exactWord)
    } else {
      finalStock = finalStock.filter((stockItem) =>
        regExp.test(stockItem.newComment || stockItem.comment)
      )
    }
  }

  const filterLocations = (
    stockItem: InventoryArticle | PricedInventoryArticle,
    matchingFunction: (locName: string) => boolean
  ) => {
    if (!stockItem.locations) {
      return false
    }

    let locationMatchesFilter = false
    for (const loc of stockItem.locations) {
      if (matchingFunction(loc.name)) {
        locationMatchesFilter = true
        break
      }
    }
    return locationMatchesFilter
  }

  if (location) {
    const wordsToFind = location
      .replace(/^!/, '')
      .replace(/^;/, '')
      .split('\n')
      .map((line) => escapeRegExp(line.trim()))
      .filter((line) => line)
      .join('|')

    const isEmptyLocationQuery =
      location.replace('!', '') === '""' || location.replace('!', '') === "''"

    const regExp = new RegExp(wordsToFind, 'i')
    if (isEmptyLocationQuery) {
      finalStock = finalStock.filter((stockItem) => {
        return location[0] === '!' ? stockItem.location !== '' : stockItem.location === ''
      })
    } else if (location[0] === '!') {
      finalStock = finalStock.filter((stockItem) => {
        return filterLocations(stockItem, (locName) => !regExp.test(locName))
      })
    } else if (location[0] === ';') {
      const exactWord = location.slice(1).trim()

      finalStock = finalStock.filter((stockItem) => {
        return filterLocations(stockItem, (locName) => locName === exactWord)
      })
    } else {
      finalStock = finalStock.filter((stockItem) => {
        return filterLocations(stockItem, (locName) => regExp.test(locName))
      })
    }
  }

  if (exactPrice) {
    finalStock = finalStock.filter((item) =>
      item.newPrice ? item.newPrice === Number(exactPrice) : item.price === Number(exactPrice)
    )
  }

  if (edited !== 'any')
    finalStock = finalStock.filter((item) =>
      edited ? isArticleEdited(item) || item.hasEditedLocations() : !isArticleEdited(item)
    )

  if (priceChanged !== 'any') {
    finalStock = finalStock.filter((item) => {
      if (priceChanged) {
        return item.newPrice !== undefined && item.newPrice !== item.price
      } else {
        return item.newPrice === undefined || item.newPrice === item.price
      }
    })
  }

  if (priceUnchanged !== 'any') {
    finalStock = finalStock.filter((item) => {
      if (item instanceof PricedInventoryArticle) {
        if (priceUnchanged) {
          // voglio solo quelli invariati
          return (
            isPriceChangeTooSmall(item) ||
            (!item.priceReport.failure &&
              (item.newPrice === undefined || item.newPrice === item.price))
          )
        } else {
          // voglio solo quelli cambiati
          return (
            !item.priceReport.failure && item.newPrice !== undefined && item.newPrice !== item.price
          )
        }
      }
      return false
    })
  }

  if (priceFailed !== 'any') {
    finalStock = finalStock.filter((item) => {
      if (item instanceof PricedInventoryArticle) {
        const failed = !!item.priceReport.failure && !isPriceChangeTooSmall(item)
        return priceFailed ? failed : !failed
      }
    })
  }

  if (finishType !== 'any') {
    if (filters.finishType !== 'any') {
      finalStock = finalStock.filter((item) => item.finishType === filters.finishType)
    }
  }

  if (graded !== 'any')
    finalStock = finalStock.filter((item) =>
      graded ? item.graded.isGraded : !item.graded.isGraded
    )

  if (american !== 'any')
    finalStock = finalStock.filter((item) =>
      american
        ? item.countryEdition === CountryEdition.US
        : item.countryEdition === CountryEdition.Regular
    )

  if (playset !== 'any')
    finalStock = finalStock.filter((item) => (playset ? item.isPlayset : !item.isPlayset))

  if (firstEd !== 'any')
    finalStock = finalStock.filter((item) => (firstEd ? item.isFirstEd : !item.isFirstEd))

  if (signed !== 'any')
    finalStock = finalStock.filter((item) => (signed ? item.isSigned : !item.isSigned))

  if (selected !== 'any')
    finalStock = finalStock.filter((item) => (selected ? item.selected : !item.selected))

  if (sold !== 'any')
    finalStock = finalStock.filter((item) =>
      sold ? item.ptArticleReservedQuantity !== 0 : item.ptArticleReservedQuantity === 0
    )

  if (condition[1] - condition[0] !== 6) {
    finalStock = finalStock.filter(
      (item) =>
        CONDITIONS_LOOKUP[item.condition] >= condition[0] &&
        CONDITIONS_LOOKUP[item.condition] <= condition[1]
    )
  }

  if (rarity[0] !== 1) {
    finalStock = finalStock.filter((item) => item.card.rarity >= rarity[0])
  }

  if (rarity[1] !== Object.keys(activeGame.rarities).length) {
    finalStock = finalStock.filter((item) => item.card.rarity <= rarity[1])
  }

  if (minQuantity) {
    finalStock = finalStock.filter((item) =>
      item.newQuantity ? item.newQuantity >= minQuantity : item.quantity >= minQuantity
    )
  }

  if (maxQuantity) {
    finalStock = finalStock.filter((item) =>
      item.newQuantity ? item.newQuantity <= maxQuantity : item.quantity <= maxQuantity
    )
  }

  if (minPrice) {
    finalStock = finalStock.filter((item) =>
      item.newPrice ? item.newPrice >= minPrice : item.price >= minPrice
    )
  }

  if (maxPrice) {
    finalStock = finalStock.filter((item) =>
      item.newPrice ? item.newPrice <= maxPrice : item.price <= maxPrice
    )
  }

  if (language !== 'any') {
    finalStock = finalStock.filter((item) => item.idLanguage === language)
  }

  if (expansion) {
    finalStock = finalStock.filter((item) => item.card.set === expansion?.code)
  }

  // setLoading(false)
  return finalStock
}
